import * as THREE from 'three';
import {cloneOwnedMaterial} from '../../material';
import { ImportedModelSelectionGeometryTemplate } from './ImportedModelSelectionGeometryTemplate';

/**
 * imported Body 整模型 Selected 覆盖 Mesh。
 *
 * 设计说明：
 * 1. 覆盖对象借用正式 source Mesh 的渲染 geometry，不创建或读取拓扑宿主；
 * 2. 覆盖对象使用独立于原模型的 Selected 材质，原始可见模型与隐藏拾取宿主都不改材质；
 * 3. 覆盖对象不复制 FeatureResultMesh children，避免再次挂载 FeatureFace、FeatureLine 等拓扑代理；
 * 4. 完整 geometry 仍由 source 持有；本对象只释放独占材质及精确 primitive 子集。
 */
export class ImportedModelBodySelectionOverlayMesh extends THREE.Mesh {
    /** 供场景诊断识别整模型 Selected 覆盖对象的稳定标记。 */
    public readonly isImportedModelBodySelectionOverlay = true;
    /** 独占材质必须随本覆盖对象释放，不能把 RenderObject 监听留在共享模板上。 */
    private readonly ownedSelectionMaterial: THREE.Material;
    /** 覆盖对象的幂等生命周期标记。 */
    private disposed = false;
    /** 精确子集的布局缓存，完整模型选择不分配 geometry。 */
    private readonly geometryTemplate = new ImportedModelSelectionGeometryTemplate();

    /**
     * 创建整模型 Selected 覆盖 Mesh。
     *
     * @param sourceHost 正式渲染来源，不是拓扑宿主。
     * @param selectedMaterial 整模型共享 Selected 配方，构造时克隆独占副本。
     */
    public constructor(
        sourceHost: THREE.Mesh,
        selectedMaterial: THREE.Material,
    ) {
        // 只借用渲染几何，不复制 source 的 userData、metadata 或 children 中的业务资产。
        super(sourceHost.geometry, selectedMaterial);
        this.ownedSelectionMaterial = cloneOwnedMaterial(selectedMaterial);
        this.material = this.ownedSelectionMaterial;
        this.userData = {};
        this.name = 'imported-body-selection-overlay:' + sourceHost.uuid;
        this.renderOrder = Math.max(sourceHost.renderOrder, 5);
        this.castShadow = false;
        this.receiveShadow = false;
        /**
         * 统一复用场景层既有的派生对象过滤协议。
         * 标记集中封装在覆盖对象内部，调用方无需散落写入 userData。
         */
        this.userData.skipSelection = true;
        this.userData.skipSelectionSubtree = true;
        this.userData.isImportedModelOverlay = true;
        try {
            this.syncFromSourceHost(sourceHost);
        } catch (error) {
            this.dispose();
            throw error;
        }
    }

    /**
     * 同步正式 Mesh 的渲染几何，直接借用其局部矩阵对象。
     *
     * 说明：
     * 选择组与 source 共用父级，组变换恒为单位矩阵。源对象自身移动或祖先移动时，
     * 共享局部矩阵会沿同一父链投影，不需要高频复制矩阵或重新分解位姿。
     *
     * @param sourceHost 当前正式渲染来源。
     * @param primitiveIndices 局部 primitive 子集；完整选择时省略。
     */
    public syncFromSourceHost(sourceHost: THREE.Mesh, primitiveIndices?: readonly number[]): void {
        if (this.disposed) throw new Error(`Body 选中覆盖对象已释放：${this.name}`);
        this.geometry = this.geometryTemplate.resolve(sourceHost.geometry, primitiveIndices);
        // 子集使用 group 绘制；完整几何保持原有共享引用。
        if (this.geometry === sourceHost.geometry) this.material = this.ownedSelectionMaterial;
        else if (!Array.isArray(this.material)) this.material = [this.ownedSelectionMaterial];
        if (sourceHost.matrixAutoUpdate) sourceHost.updateMatrix();
        this.position.copy(sourceHost.position);
        this.quaternion.copy(sourceHost.quaternion);
        this.scale.copy(sourceHost.scale);
        this.matrix = sourceHost.matrix;
        this.matrixAutoUpdate = false;
        this.matrixWorldAutoUpdate = true;
        this.frustumCulled = sourceHost.frustumCulled;
        this.layers.mask = sourceHost.layers.mask;
        this.visible = true;
        this.updateMatrixWorld(true);
    }

    /**
     * 禁止覆盖对象参与射线拾取。
     *
     * @param _raycaster 当前射线检测器。
     * @param _intersects 命中结果数组。
     */
    public override raycast(
        _raycaster: THREE.Raycaster,
        _intersects: THREE.Intersection[],
    ): void {
        // Selected 覆盖层只是视觉派生对象，拾取仍由原始可见 Mesh 和拓扑宿主负责。
    }

    /**
     * 释放场景引用和独占材质，触发 Three.js RenderObject 及其 uniform/bindings 回收。
     *
     * 说明：
     * geometry 与材质模板仍归外部所有者；这里只释放本对象创建的材质副本。
     */
    public dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.removeFromParent();
        this.ownedSelectionMaterial.dispose();
        this.geometryTemplate.dispose();
        // 解除对外部矩阵的借用，销毁覆盖对象不能继续持有来源位姿。
        this.matrix = new THREE.Matrix4();
    }
}
